import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const RetailerDashboard = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slider data
  const slides = [
    {
      title: '24x7 Payment Solutions',
      description: 'Instant payments, transfers, and recharges at your fingertips',
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: 'bi-credit-card-2-front'
    },
    {
      title: 'Grow Your Business',
      description: 'Earn commissions on every transaction with competitive rates',
      bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      icon: 'bi-graph-up-arrow'
    },
    {
      title: 'Secure & Reliable',
      description: 'Bank-grade security for all your financial transactions',
      bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      icon: 'bi-shield-check'
    }
  ];

  // Services data
  const services = [
    { name: 'Mobile Recharge', icon: 'bi-phone', color: '#667eea', path: '/mobile-recharge' },
    { name: 'DTH Recharge', icon: 'bi-tv', color: '#f5576c', path: '/dth-recharge' },
    { name: 'Bill Payments', icon: 'bi-receipt-cutoff', color: '#4facfe', path: '/bill-payments' },
    { name: 'UPI Transfer', icon: 'bi-bank', color: '#f093fb', path: '/upi-transfer' },
    { name: 'Fund Transfer', icon: 'bi-cash-stack', color: '#764ba2', path: '/fund-transfer' },
    { name: 'AEPS', icon: 'bi-fingerprint', color: '#00f2fe', path: '/aeps' },
    { name: 'Micro ATM', icon: 'bi-credit-card-2-front', color: '#f5576c', path: '/micro-atm-pos' },
    { name: 'Loan', icon: 'bi-wallet2', color: '#667eea', path: '/loan' }
  ];

  // Recent transactions data (mock data)
  const recentTransactions = [
    { id: 'TXN001', type: 'Mobile Recharge', amount: 299, status: 'Success', time: '2 mins ago' },
    { id: 'TXN002', type: 'DTH Recharge', amount: 450, status: 'Success', time: '5 mins ago' },
    { id: 'TXN003', type: 'Bill Payment', amount: 1200, status: 'Pending', time: '10 mins ago' },
    { id: 'TXN004', type: 'UPI Transfer', amount: 5000, status: 'Success', time: '15 mins ago' },
    { id: 'TXN005', type: 'Fund Transfer', amount: 2500, status: 'Success', time: '20 mins ago' },
    { id: 'TXN006', type: 'Mobile Recharge', amount: 199, status: 'Success', time: '25 mins ago' },
    { id: 'TXN007', type: 'AEPS', amount: 3000, status: 'Success', time: '30 mins ago' },
    { id: 'TXN008', type: 'Bill Payment', amount: 850, status: 'Failed', time: '35 mins ago' },
    { id: 'TXN009', type: 'DTH Recharge', amount: 350, status: 'Success', time: '40 mins ago' },
    { id: 'TXN010', type: 'Mobile Recharge', amount: 99, status: 'Success', time: '45 mins ago' }
  ];

  // Auto slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Success': return 'success';
      case 'Pending': return 'warning';
      case 'Failed': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid className="retailer-dashboard p-4">
      {/* Dashboard Header */}
      <div className="dashboard-header mb-4">
        <h2 className="mb-1">
          <i className="bi bi-speedometer2 me-2"></i>
          Retailer Dashboard
        </h2>
        <p className="text-muted mb-0">Welcome back! Here's what's happening with your business today.</p>
      </div>

      {/* Slider Section */}
      <div className="dashboard-slider mb-4">
        <div className="slider-container" style={{ background: slides[currentSlide].bgColor }}>
          <div className="slider-content">
            <div className="slider-icon">
              <i className={`bi ${slides[currentSlide].icon}`}></i>
            </div>
            <h3 className="slider-title">{slides[currentSlide].title}</h3>
            <p className="slider-description">{slides[currentSlide].description}</p>
          </div>
          <div className="slider-dots">
            {slides.map((_, index) => (
              <span
                key={index}
                className={`dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              ></span>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={4} md={6} className="mb-3">
          <Card className="stats-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1">Total Transactions</p>
                  <h3 className="mb-0">₹1,24,350</h3>
                  <small className="text-success">
                    <i className="bi bi-arrow-up"></i> 12% from last month
                  </small>
                </div>
                <div className="stats-icon" style={{ background: '#667eea' }}>
                  <i className="bi bi-receipt"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={6} className="mb-3">
          <Card className="stats-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1">Pending Transactions</p>
                  <h3 className="mb-0">08</h3>
                  <small className="text-warning">
                    <i className="bi bi-clock"></i> 3 need attention
                  </small>
                </div>
                <div className="stats-icon" style={{ background: '#f5576c' }}>
                  <i className="bi bi-hourglass-split"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={6} className="mb-3">
          <Card className="stats-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1">Retailer Outstanding</p>
                  <h3 className="mb-0">₹45,200</h3>
                  <small className="text-info">
                    <i className="bi bi-info-circle"></i> Due in 5 days
                  </small>
                </div>
                <div className="stats-icon" style={{ background: '#4facfe' }}>
                  <i className="bi bi-wallet2"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Services & Transactions Section */}
      <Row>
        {/* Left Side - Services */}
        <Col lg={6} className="mb-4">
          <Card className="services-card h-100">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-grid-3x3-gap me-2"></i>
                Quick Services
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="services-grid">
                {services.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="service-item"
                  >
                    <div className="service-icon" style={{ background: service.color }}>
                      <i className={`bi ${service.icon}`}></i>
                    </div>
                    <span className="service-name">{service.name}</span>
                  </Link>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Side - Recent Transactions */}
        <Col lg={6} className="mb-4">
          <Card className="transactions-card h-100">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Recent Transactions
              </h5>
              <Link to="/reports/ledger-reports" className="view-all-link">
                View All <i className="bi bi-arrow-right"></i>
              </Link>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="transactions-list">
                {recentTransactions.map((transaction, index) => (
                  <div key={index} className="transaction-item">
                    <div className="transaction-details">
                      <div className="transaction-id">{transaction.id}</div>
                      <div className="transaction-type">{transaction.type}</div>
                      <div className="transaction-time text-muted">
                        <i className="bi bi-clock"></i> {transaction.time}
                      </div>
                    </div>
                    <div className="transaction-info">
                      <div className="transaction-amount">₹{transaction.amount}</div>
                      <span className={`transaction-status badge bg-${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RetailerDashboard;

